/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import MsDyn365 from '@msdyn365-commerce/core';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';

import * as React from 'react';
import classnames from 'classnames';

import { ICittaJournalPostsData } from './citta-journal-posts.data';
import { ICittaJournalPostsProps } from './citta-journal-posts.props.autogenerated';
import {IGetJournalPostData } from '../../data-actions/journal/get-journal-posts';

enum Market {
    NZ = 'NZL',
    AU = 'AUS',
    US = 'USA'
}

export interface ICittaJournalPostsViewProps extends ICittaJournalPostsProps<ICittaJournalPostsData> {
    JournalsWrapper: IModuleProps;
    journals: React.ReactNode;
    loadMore: React.ReactNode;
    search: React.ReactNode;
    categories: React.ReactNode;
}

// const Categories = {
//     behindtheproduct: 'Città Behind the product',
//     styling: 'Stying'
// }

export interface ICittaJournalPostsState {
    allJournals: any;
    displayedJournals: any;
    displayCount: number;
    searchTerm: string;
    categoryFilter: string;
}

/**
 *
 * CittaJournalPosts component
 * @extends {React.PureComponent<ICittaJournalPostsProps<ICittaJournalPostsData>>}
 */
class CittaJournalPosts extends React.PureComponent<ICittaJournalPostsProps<ICittaJournalPostsData>, ICittaJournalPostsState> {
    private moduleClassName: string = 'journal-posts-wrapper';

    constructor(props: any) {
        super(props);
        const { audience } = props.config;
        const journals = props.data && props.data.journals && props.data.journals.result;
        let journalsFilteredByAudience = journals.posts && journals.posts.filter((post: any) => post.audience.indexOf(audience) != -1);

        this.state = {
            allJournals: journalsFilteredByAudience || [],
            displayedJournals: (journalsFilteredByAudience || []).slice(0, 6),
            displayCount: 6,
            searchTerm: '',
            categoryFilter: ''
        }
        this._loadMore = this._loadMore.bind(this);
        this._renderSearch = this._renderSearch.bind(this);
        this._renderCategoryFilter = this._renderCategoryFilter.bind(this);
        this._onType = this._onType.bind(this);
        this._getPostUrl = this._getPostUrl.bind(this);
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser && typeof window !== 'undefined') {
            const { categories } = this.props.config;
            const url: string = decodeURI(window.location.search);
            const urlParams = new URLSearchParams(url);
            const journalcategory = urlParams.has('journalcategory') ? urlParams.get('journalcategory') : '';
            const category = categories?.find((c: String) => c.toLocaleLowerCase() === journalcategory?.toLocaleLowerCase());
            category && this._setCategoryFilter(category)
        }

        let container = document.querySelector(".journal-posts-wrapper-categories ul");
        if (container) {
            const middle =  container.querySelector("li.active") || container.children[Math.floor((container.children.length) / 2)];
            // @ts-ignore
            container.scrollLeft = middle.offsetLeft + middle.offsetWidth / 2 - container.offsetWidth / 2;
        }
    }

    public render(): JSX.Element | null {
        const {displayedJournals, allJournals} = this.state;

        if (!allJournals || !allJournals.length) {
            //TODO: create a better page in here for no posts found or something
            return <div className="no-journals-found">No journals found at the moment</div>
        }

        const viewProps: ICittaJournalPostsViewProps = {
            ...(this.props as ICittaJournalPostsProps<ICittaJournalPostsData> ),
            JournalsWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName)
            },
            journals: this._renderPosts(displayedJournals),
            loadMore: this._renderLoadMore(),
            search: this._renderSearch(),
            categories: this._renderCategoryFilter(),
        };

        return this.props.renderView(viewProps);
    }

    private _formatDate(date: string): string {
        const d = date.split('-');
        return `${parseInt(d[2], 10)}.${parseInt(d[1], 10)}.${d[0]}`
    }

    private _renderLoadMore = () => {
        const {displayedJournals, allJournals } = this.state;
        if (displayedJournals.length >= allJournals.length) {
            return null;
        }
        return (
            <div className="journal-posts-wrapper-load-more">
                <a onClick={this._loadMore}>Load More</a>
            </div>
        )
    }

    private _onType = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { categoryFilter } = this.state;

        const newDisplayCount = 10;

        const list  = this._getFilteredJournals(e.target.value.toLowerCase(), categoryFilter)
        
        this.setState({
            displayCount: newDisplayCount,
            displayedJournals: list.slice(0, newDisplayCount),
            searchTerm: e.target.value,
        })
    }

    private _getFilteredJournals = (searchTerm: string, categoryFilter: string) =>  {
        const { allJournals } = this.state;

        let list = allJournals.filter((journal: IGetJournalPostData) => {
            if (categoryFilter && journal.category !== categoryFilter) {
                return null;
            }
            return journal.title.toLowerCase().indexOf(searchTerm) !== -1 || 
                journal.subTitle.toLowerCase().indexOf(searchTerm) !== -1 
        })

        return list
    }

    private _setCategoryFilter = (type: string) => {
        const { searchTerm } = this.state;

        const newDisplayCount = 6;

        let list = this._getFilteredJournals(searchTerm, type);
        this.setState({
            categoryFilter: type,
            displayCount: newDisplayCount,
            displayedJournals: list.slice(0, newDisplayCount)
        })
    }

    private _renderCategoryFilter = () => {
        const { categories } = this.props.config;
        const { categoryFilter } = this.state;

        return (
            <div className="journal-posts-wrapper-categories">
                 <ul>
                    <li className={!categoryFilter ? 'active' : ''} onClick={() => this._setCategoryFilter('')}>All</li>
                    {categories?.map(category => {
                        return <li className={category === categoryFilter ? 'active' : ''} onClick={() => this._setCategoryFilter(category)}>{category}</li>
                    })}
                </ul>
            </div>
        )
    }

    private _renderSearch = () => {
        return (
            <div className="journal-posts-wrapper-search">
                <input onChange={this._onType} placeholder='search'/>
            </div>
        )
    }

    private _loadMore(): void {
        const { displayCount, searchTerm,  categoryFilter } = this.state;

        const newDisplayCount = displayCount + 3;
        let list = this._getFilteredJournals(searchTerm, categoryFilter);
        this.setState({
            displayCount: newDisplayCount,
            displayedJournals: list.slice(0, newDisplayCount)
        })
    }

    private getMarket = (url: string = ''): string => {
        let indexOfCountry = 3;
        if (url.indexOf('test.') > 0 || url.indexOf('cittab.') > 0) {
            indexOfCountry = 4;
        }
        let market = (url && url.length > 0) 
            ? (url.indexOf('//') > -1 
                ? ((url.split('/')[indexOfCountry] === undefined || (url.split('/')[indexOfCountry].length > 2 && url.split('/')[indexOfCountry].toLowerCase() !== 'international' && url.split('/')[indexOfCountry].toLowerCase() !== 'us') || url.split('/')[indexOfCountry] === '') 
                    ? Market.NZ 
                    : url.split('/')[indexOfCountry].toUpperCase()) 
                : Market.NZ) 
            : '';

        return market;
    };

    private _getPostUrl = (postUrl: string) => {
        const url = this.props.context.request.url && this.props.context.request.url.requestUrl && this.props.context.request.url.requestUrl.href;

        if (postUrl.indexOf('https://') !== -1)  {
            return postUrl;
        }

        const market = this.getMarket(url);

        if (market === 'AU') {
            return `\/au${postUrl}`
        } else if (market.toLowerCase() === 'international' || market.toLowerCase() === 'us') {   
            return `/${market.toLowerCase()}${postUrl}`
        }
        
        return postUrl;
    }

    private _renderPosts = (posts: IGetJournalPostData[]) => {
        if (!posts.length) {
            return <div className="no-journals-found">We can't find any journals to match that</div>
        }

        return posts.map((post: IGetJournalPostData) => {
            return  (
               <div className="journal-post">
                   <a href={this._getPostUrl(post.postUrl)}>
                        {post.imageUrl && <img className="journal-post__img"
                            src={post.imageUrl}
                            alt={post.title}
                        />}
                        <div className="journal-post__details">
                            {this._formatDate(post.date)} |  {post.category}
                        </div>
                        <div className="journal-post__title">{post.title}</div>
                        <div className="journal-post__subTitle">{post.subTitle}</div>
                        <div className="journal-post__readmore">read more {'>'}</div>
                    </a>
                    <hr />
               </div>
            );
        })
    };
}

export default CittaJournalPosts;
